import { useState } from 'react';

interface JsonTreeProps {
  value: unknown;
  depth?: number;
  defaultExpanded?: boolean;
  testId?: string;
  path?: string;
}

function Primitive({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span style={{ color: '#aaa', fontStyle: 'italic' }}>null</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span style={{ color: value ? '#22863a' : '#cb2431', fontWeight: 600 }}>
        {String(value)}
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span style={{ color: '#005cc5' }}>{String(value)}</span>;
  }
  const str = String(value);
  if (str.startsWith('https://') || str.startsWith('http://')) {
    return (
      <a
        href={str}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--brand)', wordBreak: 'break-all' }}
      >
        {str}
      </a>
    );
  }
  return <span style={{ wordBreak: 'break-word' }}>{str}</span>;
}

export function JsonTree({ value, depth = 0, defaultExpanded, testId, path = 'root' }: JsonTreeProps) {
  const isRoot = depth === 0;
  const initiallyExpanded = defaultExpanded !== undefined ? defaultExpanded : isRoot;
  const [expanded, setExpanded] = useState(initiallyExpanded);

  if (value === null || value === undefined || typeof value !== 'object') {
    return <Primitive value={value} />;
  }

  const isArray = Array.isArray(value);
  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);

  const label = isArray
    ? `[${entries.length} item${entries.length !== 1 ? 's' : ''}]`
    : `{${entries.length} field${entries.length !== 1 ? 's' : ''}}`;

  return (
    <div data-testid={testId ? `${testId}-${path}` : undefined}>
      {!isRoot && (
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} node ${label}`}
          data-testid={testId ? `${testId}-${path}-toggle` : undefined}
          data-expanded={expanded}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            fontSize: '0.78rem',
            color: 'var(--brand)',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '0.65rem' }}>{expanded ? '▼' : '▶'}</span>
          <span style={{ color: '#888' }}>{label}</span>
        </button>
      )}

      {(isRoot || expanded) && (
        <div
          style={
            !isRoot
              ? {
                  marginLeft: '0.75rem',
                  marginTop: '0.2rem',
                  borderLeft: '2px solid #e4e8ec',
                  paddingLeft: '0.5rem',
                }
              : undefined
          }
        >
          {entries.map(([key, val], i) => {
            const isPrim =
              val === null ||
              val === undefined ||
              (typeof val !== 'object' && !Array.isArray(val));
            return (
              <div
                key={key}
                data-testid={testId ? `${testId}-${path}-${key}` : undefined}
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  padding: '3px 4px',
                  borderBottom: isRoot ? '1px solid #f4f4f4' : 'none',
                  background: isRoot ? (i % 2 === 0 ? 'white' : '#f8f9fa') : 'transparent',
                  alignItems: isPrim ? 'center' : 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    color: isArray ? '#aaa' : '#666',
                    fontWeight: isArray ? 400 : 500,
                    fontSize: '0.78rem',
                    minWidth: isRoot ? '180px' : '80px',
                    flexShrink: 0,
                  }}
                >
                  {isArray ? `[${key}]` : key}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: '0.78rem' }}>
                  <JsonTree value={val} depth={depth + 1} testId={testId} path={`${path}-${key}`} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
